/*
 * Settings, Schedule and field: routes (the Route view in the Schedule, on by default for lawn) and where the teams start
 * the day, capacity (teams per weekday, never for lawn), the arrival window clients hear, colors by word on the Schedule,
 * and the On my way guess when the drive time is not known.
 */
import { useState } from "react";
import { arrivalTimes, routesOn, type Settings } from "@steward/shared";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { timeRange } from "../../components/format";
import { InputText } from "../../components/fields";
import { useToast } from "../../components/overlays";
import { Button, Card, Details, SettingRow, Skeleton, Switch } from "../../components/parts";
import { errText } from "../../components/work";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function ScheduleSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [cap, setCap] = useState<Settings["capacity"] | null>(null);
  const [field, setField] = useState<Settings["field"] | null>(null);
  const [start, setStart] = useState<string | null>(null);
  if (!s.data) return <Skeleton rows={6} />;
  const owner = can("settings.update");
  // Auto scheduling (Booking from a quote) keeps capacity on, even for a route business.
  const autoBook = s.data.settings.booking.fromQuote;
  const lawn = company.trade === "lawn" && !autoBook;
  const c = s.data.settings.capacity;
  const f = s.data.settings.field;
  const done = { onSuccess: () => toast("Saved"), onError: (e: unknown) => toast(errText(e), { kind: "error" }) };

  const routes = routesOn(s.data.settings, company.trade);

  return (
    <>
      <Card
        id="routes"
        title="Routes"
        action={
          owner && routes && start === null ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setStart(f.startAddress)}>
              Edit
            </Button>
          ) : null
        }
      >
        <div className="stack">
          <SettingRow
            title="Use routes"
            description={
              lawn
                ? "Jobs run on named routes, each on its weekday, with stops in order. The Schedule gets a Route view with a map and Best order."
                : "For work done on set days in set areas, like lawn care. The Schedule gets a Route view with a map and Best order."
            }
            changed={s.data.settings.routes.on !== null}
            control={
              <Switch
                label="Use routes"
                checked={routes}
                disabled={!owner || save.isPending}
                onChange={(on) => save.mutate({ routes: { on } }, done)}
              />
            }
          />
          {routes ? (
            start !== null ? (
              <div className="stack">
                <InputText
                  label="Where the teams start the day"
                  description="Best order and the Google Maps links start and end here."
                  wide
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
                <div className="row end">
                  <Button type="tertiary" onClick={() => setStart(null)}>
                    Cancel
                  </Button>
                  <Button
                    loading={save.isPending}
                    onClick={() =>
                      save.mutate(
                        { field: { startAddress: start.trim() } },
                        {
                          ...done,
                          onSuccess: () => {
                            setStart(null);
                            toast("Saved");
                          },
                        },
                      )
                    }
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <Details
                rows={[["Teams start from", f.startAddress || "Not set. Best order guesses by straight lines until it is."]]}
              />
            )
          ) : null}
        </div>
      </Card>

      <Card
        id="capacity"
        title="Capacity"
        action={
          owner && !lawn && !cap ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setCap({ ...c })}>
              Edit
            </Button>
          ) : null
        }
      >
        {lawn ? (
          <p className="muted">Route businesses do not use capacity. Each route runs on its own day.</p>
        ) : cap ? (
          <div className="stack">
            <Switch
              checked={cap.on || autoBook}
              disabled={autoBook}
              label="Close a day when it is full"
              onChange={(on) => setCap({ ...cap, on })}
            />
            {autoBook ? (
              <p className="muted small">On while auto scheduling is on. Clients book only the days and times with room.</p>
            ) : null}
            <div className="row">
              {DAYS.map((d, i) => (
                <InputText
                  key={d}
                  label={`${d} teams`}
                  inputMode="numeric"
                  value={String(cap.teamsByWeekday[i] ?? 0)}
                  onChange={(e) => {
                    const teams = [...cap.teamsByWeekday];
                    teams[i] = Number(e.target.value.replace(/\D/g, "") || "0");
                    setCap({ ...cap, teamsByWeekday: teams });
                  }}
                />
              ))}
            </div>
            <div className="row">
              <InputText
                label="People on a team"
                inputMode="numeric"
                value={String(cap.teamSize)}
                onChange={(e) => setCap({ ...cap, teamSize: Number(e.target.value.replace(/\D/g, "") || "1") })}
              />
              <InputText
                label="Hours each person works"
                inputMode="decimal"
                value={String(cap.hoursEach)}
                onChange={(e) => setCap({ ...cap, hoursEach: Number(e.target.value) || cap.hoursEach })}
              />
              <InputText
                label="Hours a job takes when not typed"
                inputMode="decimal"
                value={String(cap.defaultJobHours)}
                onChange={(e) => setCap({ ...cap, defaultJobHours: Number(e.target.value) || cap.defaultJobHours })}
              />
            </div>
            <div className="row end">
              <Button type="tertiary" onClick={() => setCap(null)}>
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  save.mutate(
                    { capacity: cap },
                    {
                      ...done,
                      onSuccess: () => {
                        setCap(null);
                        toast("Saved");
                      },
                    },
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <Details
            rows={[
              ["Close full days", c.on ? (autoBook ? "On, auto scheduling needs it" : "On") : "Off"],
              ["Teams", DAYS.map((d, i) => `${d} ${String(c.teamsByWeekday[i] ?? 0)}`).join(", ")],
              ["Team size", `${String(c.teamSize)} people, ${String(c.hoursEach)} hours each`],
              ["A job with no hours", `${String(c.defaultJobHours)} hours`],
            ]}
          />
        )}
      </Card>

      <ArrivalCard settings={s.data.settings} owner={owner} />
      <ColorsCard settings={s.data.settings} owner={owner} />
      <Card
        id="field"
        title="In the field"
        action={
          owner && !field ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setField({ ...f })}>
              Edit
            </Button>
          ) : null
        }
      >
        {field ? (
          <div className="stack">
            <InputText
              label="On my way guess, in minutes, when the drive is not known"
              inputMode="numeric"
              value={String(field.onMyWayMinutes)}
              onChange={(e) => setField({ ...field, onMyWayMinutes: Number(e.target.value.replace(/\D/g, "") || "20") })}
            />
            <div className="row end">
              <Button type="tertiary" onClick={() => setField(null)}>
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  save.mutate(
                    { field },
                    {
                      ...done,
                      onSuccess: () => {
                        setField(null);
                        toast("Saved");
                      },
                    },
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <Details rows={[["On my way guess", `${String(f.onMyWayMinutes)} minutes`]]} />
        )}
      </Card>
    </>
  );
}

/** The arrival window clients hear when a job has none of its own (old Steward "Arrival window"). */
function ArrivalCard({ settings, owner }: { settings: Settings; owner: boolean }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<{ before: string; after: string } | null>(null);
  const { arriveBefore, arriveAfter } = settings.schedule;
  const sample = arrivalTimes({ arriveFrom: null, arriveTo: null, startTime: "09:00" }, settings);
  const mins = (v: string) => Math.min(240, Number(v.replace(/\D/g, "") || "0"));
  return (
    <Card
      id="arrival"
      title="Arrival window"
      action={
        owner && !draft ? (
          <Button
            type="secondary"
            size="small"
            icon="edit"
            onClick={() => setDraft({ before: String(arriveBefore), after: String(arriveAfter) })}
          >
            Edit
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack">
          <div className="row">
            <InputText
              label="Minutes before the start"
              inputMode="numeric"
              suffix="min"
              value={draft.before}
              onChange={(e) => setDraft({ ...draft, before: e.target.value })}
            />
            <InputText
              label="Minutes after the start"
              inputMode="numeric"
              suffix="min"
              value={draft.after}
              onChange={(e) => setDraft({ ...draft, after: e.target.value })}
            />
          </div>
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { schedule: { arriveBefore: mins(draft.before), arriveAfter: mins(draft.after) } },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      toast("Saved");
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <Details
          rows={[
            [
              "Clients hear",
              arriveBefore || arriveAfter
                ? `${String(arriveBefore)} minutes before to ${String(arriveAfter)} minutes after the start`
                : "The start time only",
            ],
            ["A 9 AM visit says", sample ? timeRange(sample[0], sample[1] ?? null) : null],
          ]}
        />
      )}
      <p className="muted small">Used in the Job scheduled and Visit reminder messages. A job with its own window keeps it.</p>
    </Card>
  );
}

const COLOR_CHOICES = ["#2E7D32", "#1565C0", "#6A1B9A", "#C62828", "#EF6C00", "#00838F", "#AD1457", "#5D4037"];

/** Color visits on the Schedule by a word in the job's name (old Steward "Calendar colors"). */
function ColorsCard({ settings, owner }: { settings: Settings; owner: boolean }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<Settings["schedule"]["colors"] | null>(null);
  const list = settings.schedule.colors;
  const clean = (d: Settings["schedule"]["colors"]) => d.map((c) => ({ ...c, word: c.word.trim() })).filter((c) => c.word);
  const setAt = (i: number, patch: Partial<Settings["schedule"]["colors"][number]>) =>
    draft && setDraft(draft.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  return (
    <Card
      id="colors"
      title="Colors by word"
      action={
        owner && !draft ? (
          <Button type="secondary" size="small" icon="edit" onClick={() => setDraft(list.map((c) => ({ ...c })))}>
            Edit
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack">
          {draft.map((c, i) => (
            <div key={i} className="row color-word">
              <InputText
                label={`Word ${String(i + 1)}`}
                value={c.word}
                maxLength={40}
                placeholder="Deep clean"
                onChange={(e) => setAt(i, { word: e.target.value })}
              />
              <label className="field">
                <span className="label">Color</span>
                <input
                  type="color"
                  className="color-pick"
                  value={c.color}
                  onChange={(e) => setAt(i, { color: e.target.value.toUpperCase() })}
                />
              </label>
              <Button type="tertiary" size="small" icon="trash" onClick={() => setDraft(draft.filter((_, k) => k !== i))}>
                Remove
              </Button>
            </div>
          ))}
          {draft.length < 30 ? (
            <div>
              <Button
                type="secondary"
                size="small"
                icon="plus"
                onClick={() =>
                  setDraft([...draft, { word: "", color: COLOR_CHOICES[draft.length % COLOR_CHOICES.length] ?? "#1565C0" }])
                }
              >
                Add a word
              </Button>
            </div>
          ) : null}
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { schedule: { colors: clean(draft) } },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      toast("Saved");
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : list.length ? (
        <>
          <ul className="color-words">
            {list.map((c) => (
              <li key={c.word}>
                <span className="swatch" style={{ background: c.color }} />
                {c.word}
              </li>
            ))}
          </ul>
          <p className="muted small">A visit takes the first color whose word is in its job name.</p>
        </>
      ) : (
        <p className="muted">
          None yet, so visits use the job kind colors. Add a word like Deep clean or Move out to give those visits their own
          color.
        </p>
      )}
    </Card>
  );
}
